import React, { useEffect } from 'react';
import { URLS } from "@/lib/urls";
import AdminLayout from "@/components/layout/admin/adminLayout";
import { useParams } from "react-router-dom";
import { refundApi } from "@/api/refund";
import {
    Alert,
    Button,
    Card,
    Form,
    Input,
    Popconfirm,
    Select,
    Skeleton,
    Typography,
} from "antd";
import {
    CalendarDays,
    FileText,
    Layers3,
    Save,
} from "lucide-react";
import {formatDateFull} from "@/lib/utils/date";
import {formatDate} from "@/lib/helper/date";
import TableSearch from "@/components/table/tableSearch";
import RefundItemsListComponent from "@/pages/admin/refund/items/_section/RefundItemsListComponent";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import {useNavigate} from "react-router-dom";
const { Text } = Typography;
const { TextArea } = Input;

const STATUS_OPTIONS = [
    { value: 'pending', label: 'در انتظار بررسی' },
    { value: 'approved', label: 'تأیید شده' },
    { value: 'rejected', label: 'رد شده' },
    { value: 'cancelled', label: 'لغو شده' },
    { value: 'payed', label: 'پرداخت شده' },
];

function AdminRefoundGroupEditPage() {
    const { id } = useParams();
    const [form] = Form.useForm();

    const navigate = useNavigate()

    const {
        data: refundData,
        isPending,
        isError,
    } = refundApi.useAdminGetRefundGroup({ id });

    const {
        mutate: updateRefundGroup,
        isPending: isPendingUpdate,
    } = refundApi.useAdminUpdateGroup();

    const {
        mutate: removeRefundGroup,
        isPending: isPendingRemove,
    } = refundApi.useAdminDeleteGroup();


    const response = refundData?.data ?? refundData;
    const group = response?.data ?? response;

    useEffect(() => {
        if (!group) return;

        form.setFieldsValue({
            title: group.title ?? '',
            description: group.description ?? '',
            status: group.status,
        });
    }, [group, form]);

    const handleSubmit = (values) => {
        updateRefundGroup({
            id,
            title: values.title.trim(),
            description: values.description?.trim() || null,
            status: values.status,
        }, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r)
            },
            onError: (r) => {
                ApiHandleErrorMessage(r)
            }
        });
    };


    function handleDelete() {
        removeRefundGroup({
            id: id
        },
            {
                onSuccess: (r) => {

                    ApiHandleSuccessMessage(r)
                    navigate(URLS.admin.refund.group.list)
                },
                onError: (r) => {
                    ApiHandleErrorMessage(r)
                }
            })
    }

    return (
        <AdminLayout
            pageTitle={`ویرایش گروه بازگشت وجه #${id}`}
            backTo={URLS.admin.refund.dashboard}
        >
            <div className="space-y-5 pb-8" dir="rtl">
                {/* Group information */}
                <Card
                    title={
                        <div className="flex items-center gap-2 py-1">
                            <Layers3
                                size={19}
                                className="text-indigo-600 dark:text-indigo-400"
                            />
                            <span className="font-semibold">
                                اطلاعات گروه بازگشت وجه
                            </span>
                        </div>
                    }
                    bordered={false}
                    className="!rounded-2xl !bg-white !shadow-sm dark:!bg-[#171a21]"
                    styles={{
                        header: {
                            borderBottom: '1px solid var(--ant-color-border-secondary)',
                        },
                        body: {
                            padding: 20,
                        },
                    }}
                >
                    {isPending ? (
                        <Skeleton active paragraph={{ rows: 4 }} />
                    ) : isError || !group ? (
                        <Alert
                            type="error"
                            showIcon
                            message="دریافت اطلاعات گروه با خطا مواجه شد."
                            description="لطفاً صفحه را مجدداً بارگذاری کنید."
                        />
                    ) : (
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            requiredMark={false}
                        >
                            <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
                                <Form.Item
                                    name="title"
                                    label="عنوان گروه"
                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message: 'عنوان گروه را وارد کنید.',
                                        },
                                        {
                                            max: 255,
                                            message: 'حداکثر ۲۵۵ کاراکتر مجاز است.',
                                        },
                                    ]}
                                >
                                    <Input
                                        size="middle"
                                        placeholder="عنوان گروه بازگشت وجه"
                                        disabled={isPendingUpdate}
                                        prefix={
                                            <Layers3
                                                size={16}
                                                className="ml-1 text-gray-400"
                                            />
                                        }
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="status"
                                    label="وضعیت گروه"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'وضعیت گروه را انتخاب کنید.',
                                        },
                                    ]}
                                >
                                    <Select
                                        size="middle"
                                        options={STATUS_OPTIONS}
                                        placeholder="انتخاب وضعیت"
                                        disabled={isPendingUpdate}
                                    />
                                </Form.Item>
                            </div>

                            <Form.Item
                                name="description"
                                label="توضیحات"
                                className="!mb-4"
                            >
                                <TextArea
                                    rows={2}
                                    maxLength={2000}
                                    showCount
                                    placeholder="توضیحات گروه (اختیاری)"
                                    disabled={isPendingUpdate}
                                />
                            </Form.Item>

                            {/* Read-only dates */}
                            <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/5">
                                    <CalendarDays
                                        size={18}
                                        className="shrink-0 text-gray-400"
                                    />
                                    <div className="min-w-0">
                                        <Text className="!block !text-xs !text-gray-500 dark:!text-gray-400">
                                            تاریخ شروع
                                        </Text>
                                        <p className="mt-1 truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                                            {formatDate(group.start_date) || 'ثبت نشده'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/5">
                                    <CalendarDays
                                        size={18}
                                        className="shrink-0 text-gray-400"
                                    />
                                    <div className="min-w-0">
                                        <Text className="!block !text-xs !text-gray-500 dark:!text-gray-400">
                                            تاریخ پایان
                                        </Text>
                                        <p className="mt-1 truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                                            {formatDate(group.end_date) || 'ثبت نشده'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between border-t border-gray-100 pt-4 dark:border-white/10">
                                <Popconfirm title={'آیا از حذف گروه اطمینان دارید؟'}

                                            onConfirm={() => handleDelete()}
                                >
                                    <Button
                                        danger
                                        type="primary"
                                        htmlType="submit"

                                        loading={isPendingRemove}
                                    >
                                        حذف
                                    </Button>
                                </Popconfirm>



                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<Save size={16} />}
                                    loading={isPendingUpdate}
                                >
                                    ذخیره تغییرات
                                </Button>
                            </div>
                        </Form>
                    )}
                </Card>

                {/* Refund requests list will be rendered here */}
                <Card
                    title={
                        <div className="flex items-center gap-2 py-1">
                            <FileText
                                size={19}
                                className="text-indigo-600 dark:text-indigo-400"
                            />
                            <span className="font-semibold">
                                درخواست‌های بازگشت وجه این گروه
                            </span>
                        </div>
                    }

                    className="!rounded-2xl !bg-white !shadow-sm dark:!bg-[#171a21]"
                    styles={{
                        header: {
                            borderBottom: '1px solid var(--ant-color-border-secondary)',
                        },
                        body: {
                            padding: 20,
                        },
                    }}
                >

                    <RefundItemsListComponent refundGroupId={id}  />

                </Card>
            </div>
        </AdminLayout>
    );
}

export default AdminRefoundGroupEditPage;
